// src/pages/admin/CareerManagement.tsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  Add,
  Search,
  Refresh,
  Edit,
  Close,
  Work,
  LocationOn,
  AccessTime,
  People,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Description,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListCareersQuery,
  useAddCareerMutation,
  useUpdateCareerMutation,
  useDeleteCareerMutation,
  useListApplicationsQuery,
  useUpdateApplicationStatusMutation,
  useDeleteApplicationMutation,
  Career,
  CareerFormData,
  Application,
} from '../../features/admin/careerApi.ts';

// ── Constants ─────────────────────────────────────────────────────────────────

const JOB_TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship', 'Remote'];

const EMPTY_FORM: CareerFormData = {
  title: '',
  location: '',
  type: '',
  experience: '',
  description: '',
  is_active: 1,
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const getAppStatusStyle = (status: string) => {
  switch (status) {
    case 'selected':
      return { bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600 };
    case 'rejected':
      return { bgcolor: '#ffcdd2', color: '#c62828', fontWeight: 600 };
    default:
      return { bgcolor: '#fff3cd', color: '#856404', fontWeight: 600 };
  }
};

const getAppStatusIcon = (status: string) => {
  switch (status) {
    case 'selected':  return <CheckCircle sx={{ fontSize: 14 }} />;
    case 'rejected':  return <Cancel sx={{ fontSize: 14 }} />;
    default:          return <HourglassEmpty sx={{ fontSize: 14 }} />;
  }
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

// ── Component ─────────────────────────────────────────────────────────────────

const CareerManagement: React.FC = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [tabValue,       setTabValue]       = useState(0);
  const [jobTab,         setJobTab]         = useState(0);
  const [appTab,         setAppTab]         = useState(0);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [openAddDialog,  setOpenAddDialog]  = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState<Career | null>(null);
  const [formData,       setFormData]       = useState<CareerFormData>(EMPTY_FORM);
  const [formErrors,     setFormErrors]     = useState<Partial<Record<keyof CareerFormData, string>>>({});
  const [snackbar,       setSnackbar]       = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // ── API hooks ──────────────────────────────────────────────────────────────

  const { data: careersData,      isLoading: careersLoading, isError: careersError, refetch: refetchCareers }           = useListCareersQuery();
  const { data: applicationsData, isLoading: appsLoading,    isError: appsError,    refetch: refetchApplications }      = useListApplicationsQuery();

  const [addCareer,         { isLoading: isAdding }]          = useAddCareerMutation();
  const [updateCareer,      { isLoading: isUpdating }]        = useUpdateCareerMutation();
  const [deleteCareer,      { isLoading: isDeleting }]        = useDeleteCareerMutation();
  const [updateAppStatus,   { isLoading: isUpdatingStatus }]  = useUpdateApplicationStatusMutation();
  const [deleteApplication, { isLoading: isDeletingApp }]     = useDeleteApplicationMutation();

  const careers      = careersData?.data      ?? [];
  const applications = applicationsData?.data ?? [];

  // ── Resume URL helper ──────────────────────────────────────────────────────

  const getResumeUrl = (app: Application): string | null => {
    // Prefer resume_url returned directly from API (full URL)
    if (app.resume_url) return app.resume_url;
    // Fallback: build manually
    if (!app.resume) return null;
    const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT || '';
    return `${baseUrl}/uploads/resumes/${app.resume}`;
  };

  const handleViewResume = (app: Application) => {
    const url = getResumeUrl(app);
    if (!url) {
      showSnackbar('No resume uploaded', 'error');
      return;
    }
    window.open(url, '_blank');
  };

  // ── Filtering ──────────────────────────────────────────────────────────────

  const filteredCareers = careers.filter((c) => {
    const matchesSearch =
      c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.location ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.type     ?? '').toLowerCase().includes(searchTerm.toLowerCase());

    if (jobTab === 1) return matchesSearch && c.is_active === 1;
    if (jobTab === 2) return matchesSearch && c.is_active === 0;
    return matchesSearch;
  });

  const filteredApplications = applications.filter((a) => {
    const matchesSearch =
      a.name.toLowerCase().includes(searchTerm.toLowerCase())       ||
      a.email.toLowerCase().includes(searchTerm.toLowerCase())      ||
      (a.job_title ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.job_type  ?? '').toLowerCase().includes(searchTerm.toLowerCase());

    if (appTab === 1) return matchesSearch && a.status === 'pending';
    if (appTab === 2) return matchesSearch && a.status === 'selected';
    if (appTab === 3) return matchesSearch && a.status === 'rejected';
    return matchesSearch;
  });

  // ── Counts ─────────────────────────────────────────────────────────────────

  const jobCounts = {
    all:      careers.length,
    active:   careers.filter(c => c.is_active === 1).length,
    inactive: careers.filter(c => c.is_active === 0).length,
  };

  const appCounts = {
    all:      applications.length,
    pending:  applications.filter(a => a.status === 'pending').length,
    selected: applications.filter(a => a.status === 'selected').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
  };

  // ── Form helpers ──────────────────────────────────────────────────────────

  const resetForm = () => { setFormData(EMPTY_FORM); setFormErrors({}); };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof CareerFormData, string>> = {};
    if (!formData.title.trim()) errors.title = 'Job title is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof CareerFormData]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Add ────────────────────────────────────────────────────────────────────

  const handleOpenAdd  = () => { resetForm(); setOpenAddDialog(true); };
  const handleCloseAdd = () => { setOpenAddDialog(false); resetForm(); };

  const handleAdd = async () => {
    if (!validateForm()) return;
    try {
      await addCareer(formData).unwrap();
      showSnackbar('Job added successfully!', 'success');
      handleCloseAdd();
      refetchCareers();
    } catch (err: any) {
      showSnackbar(err?.data?.message || 'Error adding job. Please try again.', 'error');
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────

  const handleOpenEdit = (career: Career) => {
    setSelectedCareer(career);
    setFormData({
      title:       career.title,
      location:    career.location    ?? '',
      type:        career.type        ?? '',
      experience:  career.experience  ?? '',
      description: career.description ?? '',
      is_active:   career.is_active,
    });
    setFormErrors({});
    setOpenEditDialog(true);
  };

  const handleCloseEdit = () => { setOpenEditDialog(false); setSelectedCareer(null); resetForm(); };

  const handleUpdate = async () => {
    if (!validateForm() || !selectedCareer) return;
    try {
      await updateCareer({ id: selectedCareer.id, ...formData }).unwrap();
      showSnackbar('Job updated successfully!', 'success');
      handleCloseEdit();
      refetchCareers();
    } catch (err: any) {
      showSnackbar(err?.data?.message || 'Error updating job. Please try again.', 'error');
    }
  };

  // ── Delete career ─────────────────────────────────────────────────────────

  const handleDeleteCareer = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    try {
      await deleteCareer(id).unwrap();
      showSnackbar('Job deleted successfully!', 'success');
      refetchCareers();
    } catch (err: any) {
      showSnackbar(err?.data?.message || 'Error deleting job.', 'error');
    }
  };

  // ── Application actions ───────────────────────────────────────────────────

  const handleUpdateStatus = async (id: number, status: 'pending' | 'selected' | 'rejected') => {
    try {
      await updateAppStatus({ id, status }).unwrap();
      showSnackbar(`Application marked as ${status}`, 'success');
      refetchApplications();
    } catch (err: any) {
      showSnackbar(err?.data?.message || 'Error updating status.', 'error');
    }
  };

  const handleDeleteApplication = async (id: number) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await deleteApplication(id).unwrap();
      showSnackbar('Application deleted successfully!', 'success');
      refetchApplications();
    } catch (err: any) {
      showSnackbar(err?.data?.message || 'Error deleting application.', 'error');
    }
  };

  // ── Snackbar ──────────────────────────────────────────────────────────────

  const showSnackbar = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  const handleCloseSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

  // ── Tab styles ────────────────────────────────────────────────────────────

  const tabSx = {
    '& .MuiTab-root':       { textTransform: 'none', fontWeight: 500, fontSize: '14px', minHeight: 48, color: '#6d7175' },
    '& .Mui-selected':      { color: '#202223', fontWeight: 600 },
    '& .MuiTabs-indicator': { backgroundColor: '#008060', height: 3 },
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={() => setMobileOpen(!mobileOpen)} />
        </Box>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
        >
          <Slidemenu onToggle={() => setMobileOpen(false)} mobileOpen={false} />
        </Drawer>

        {/* Main */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={() => setMobileOpen(!mobileOpen)} />
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

              {/* Page header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Career Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => { refetchCareers(); refetchApplications(); }}
                    sx={{ textTransform: 'none', borderColor: '#c9cccf', color: '#202223', borderRadius: '8px', fontWeight: 500, '&:hover': { borderColor: '#202223', bgcolor: 'transparent' } }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenAdd}
                    sx={{ bgcolor: '#008060', color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2.5, '&:hover': { bgcolor: '#006e52' } }}
                  >
                    Add Job
                  </Button>
                </Box>
              </Box>

              {/* Top-level tabs */}
              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs value={tabValue} onChange={(_, v) => { setTabValue(v); setSearchTerm(''); }} sx={{ px: 2, ...tabSx }}>
                    <Tab label={`Job Postings (${jobCounts.all})`} />
                    <Tab label={`Applications (${appCounts.all})`} />
                  </Tabs>
                </Box>

                {/* ── JOBS TAB ─────────────────────────────────────────────── */}
                {tabValue === 0 && (
                  <>
                    <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                      <Tabs value={jobTab} onChange={(_, v) => setJobTab(v)} sx={{ px: 2, ...tabSx }}>
                        <Tab label={`All (${jobCounts.all})`} />
                        <Tab label={`Active (${jobCounts.active})`} />
                        <Tab label={`Inactive (${jobCounts.inactive})`} />
                      </Tabs>
                    </Box>

                    <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5' }}>
                      <TextField
                        size="small"
                        placeholder="Search by title, location, type..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" sx={{ color: '#6d7175' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {careersLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                        <CircularProgress size={32} sx={{ color: '#008060' }} />
                      </Box>
                    )}

                    {careersError && !careersLoading && (
                      <Box sx={{ p: 3, bgcolor: 'white' }}>
                        <Alert severity="error">
                          Failed to load jobs. Please try again.
                          <Button color="inherit" size="small" onClick={() => refetchCareers()} sx={{ ml: 1 }}>Retry</Button>
                        </Alert>
                      </Box>
                    )}

                    {!careersLoading && !careersError && filteredCareers.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                        <Work sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>No job postings found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Get started by creating your first job posting</Typography>
                        <Button variant="contained" onClick={handleOpenAdd} sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#006e52' } }}>
                          Add Job
                        </Button>
                      </Box>
                    )}

                    {!careersLoading && filteredCareers.length > 0 && (
                      <Box sx={{ bgcolor: 'white' }}>
                        {!isMobile ? (
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#fafbfb' }}>
                                  {['Title', 'Location', 'Type', 'Experience', 'Status', 'Created', 'Actions'].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175', ...(h === 'Actions' ? { textAlign: 'right' } : {}) }}>
                                      {h}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredCareers.map(career => (
                                  <TableRow key={career.id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease' }}>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{career.title}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {career.location && <LocationOn sx={{ fontSize: 14, color: '#6d7175' }} />}
                                        <Typography variant="body2">{career.location || '-'}</Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{career.type || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {career.experience && <AccessTime sx={{ fontSize: 14, color: '#6d7175' }} />}
                                        <Typography variant="body2">{career.experience || '-'}</Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={career.is_active === 1 ? 'Active' : 'Inactive'}
                                        size="small"
                                        sx={{
                                          fontSize: '12px',
                                          ...(career.is_active === 1
                                            ? { bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600 }
                                            : { bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600 }),
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                        {formatDate(career.created_at)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                        <Tooltip title="Edit">
                                          <IconButton size="small" onClick={() => handleOpenEdit(career)} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                            <Edit fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                          <IconButton size="small" onClick={() => handleDeleteCareer(career.id)} disabled={isDeleting} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Box sx={{ p: 2 }}>
                            {filteredCareers.map(career => (
                              <Card key={career.id} sx={{ mb: 2, p: 2, borderRadius: '12px', border: '1px solid #e1e3e5' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{career.title}</Typography>
                                  <Chip
                                    label={career.is_active === 1 ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{ fontSize: '10px', ...(career.is_active === 1 ? { bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600 } : { bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600 }) }}
                                  />
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
                                  <Box><Typography variant="caption" color="text.secondary">Location</Typography><Typography variant="body2">{career.location || '-'}</Typography></Box>
                                  <Box><Typography variant="caption" color="text.secondary">Type</Typography><Typography variant="body2">{career.type || '-'}</Typography></Box>
                                  <Box><Typography variant="caption" color="text.secondary">Experience</Typography><Typography variant="body2">{career.experience || '-'}</Typography></Box>
                                  <Box><Typography variant="caption" color="text.secondary">Created</Typography><Typography variant="body2">{formatDate(career.created_at)}</Typography></Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                  <IconButton size="small" onClick={() => handleOpenEdit(career)} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}><Edit fontSize="small" /></IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteCareer(career.id)} disabled={isDeleting} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}><Delete fontSize="small" /></IconButton>
                                </Box>
                              </Card>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}

                {/* ── APPLICATIONS TAB ──────────────────────────────────────── */}
                {tabValue === 1 && (
                  <>
                    <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                      <Tabs value={appTab} onChange={(_, v) => setAppTab(v)} sx={{ px: 2, ...tabSx }}>
                        <Tab label={`All (${appCounts.all})`} />
                        <Tab label={`Pending (${appCounts.pending})`} />
                        <Tab label={`Selected (${appCounts.selected})`} />
                        <Tab label={`Rejected (${appCounts.rejected})`} />
                      </Tabs>
                    </Box>

                    <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5' }}>
                      <TextField
                        size="small"
                        placeholder="Search by name, email, job title, job type..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' } }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" sx={{ color: '#6d7175' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {appsLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                        <CircularProgress size={32} sx={{ color: '#008060' }} />
                      </Box>
                    )}

                    {appsError && !appsLoading && (
                      <Box sx={{ p: 3, bgcolor: 'white' }}>
                        <Alert severity="error">
                          Failed to load applications.
                          <Button color="inherit" size="small" onClick={() => refetchApplications()} sx={{ ml: 1 }}>Retry</Button>
                        </Alert>
                      </Box>
                    )}

                    {!appsLoading && !appsError && filteredApplications.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                        <People sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>No applications found</Typography>
                      </Box>
                    )}

                    {!appsLoading && filteredApplications.length > 0 && (
                      <Box sx={{ bgcolor: 'white' }}>
                        {!isMobile ? (
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#fafbfb' }}>
                                  {['Applicant', 'Email', 'Job Title', 'Job Type', 'Resume', 'Applied On', 'Status', 'Actions'].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175', ...(h === 'Actions' ? { textAlign: 'right' } : {}) }}>
                                      {h}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {filteredApplications.map(app => (
                                  <TableRow key={app.id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease' }}>
                                    <TableCell>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{app.name}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="text.secondary">{app.email}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{app.job_title || '-'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={app.job_type || '-'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '11px', fontWeight: 500 }}
                                      />
                                    </TableCell>

                                    {/* ── Resume cell — uses full resume_url from API ── */}
                                    <TableCell>
                                      {(app.resume_url || app.resume) ? (
                                        <Button
                                          size="small"
                                          startIcon={<Description fontSize="small" />}
                                          onClick={() => handleViewResume(app)}
                                          sx={{ textTransform: 'none', fontSize: '12px', color: '#008060' }}
                                        >
                                          View Resume
                                        </Button>
                                      ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>No resume</Typography>
                                      )}
                                    </TableCell>

                                    <TableCell>
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                        {formatDate(app.created_at)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={app.status}
                                        size="small"
                                        icon={getAppStatusIcon(app.status)}
                                        sx={{ fontSize: '12px', textTransform: 'capitalize', ...getAppStatusStyle(app.status) }}
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                        {app.status !== 'selected' && (
                                          <Tooltip title="Mark as Selected">
                                            <IconButton size="small" onClick={() => handleUpdateStatus(app.id, 'selected')} disabled={isUpdatingStatus} sx={{ color: '#00783e', '&:hover': { bgcolor: '#d1f4e0' } }}>
                                              <CheckCircle fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        {app.status !== 'rejected' && (
                                          <Tooltip title="Mark as Rejected">
                                            <IconButton size="small" onClick={() => handleUpdateStatus(app.id, 'rejected')} disabled={isUpdatingStatus} sx={{ color: '#c62828', '&:hover': { bgcolor: '#ffcdd2' } }}>
                                              <Cancel fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        {app.status !== 'pending' && (
                                          <Tooltip title="Reset to Pending">
                                            <IconButton size="small" onClick={() => handleUpdateStatus(app.id, 'pending')} disabled={isUpdatingStatus} sx={{ color: '#856404', '&:hover': { bgcolor: '#fff3cd' } }}>
                                              <HourglassEmpty fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        <Tooltip title="Delete">
                                          <IconButton size="small" onClick={() => handleDeleteApplication(app.id)} disabled={isDeletingApp} sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                            <Delete fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          // Mobile cards
                          <Box sx={{ p: 2 }}>
                            {filteredApplications.map(app => (
                              <Card key={app.id} sx={{ mb: 2, p: 2, borderRadius: '12px', border: '1px solid #e1e3e5' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>{app.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{app.email}</Typography>
                                  </Box>
                                  <Chip label={app.status} size="small" sx={{ fontSize: '10px', textTransform: 'capitalize', ...getAppStatusStyle(app.status) }} />
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Job Title</Typography>
                                    <Typography variant="body2">{app.job_title || '-'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Job Type</Typography>
                                    <Typography variant="body2">{app.job_type || '-'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Applied On</Typography>
                                    <Typography variant="body2">{formatDate(app.created_at)}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">Resume</Typography>
                                    {(app.resume_url || app.resume) ? (
                                      <Button
                                        size="small"
                                        onClick={() => handleViewResume(app)}
                                        sx={{ textTransform: 'none', fontSize: '11px', color: '#008060', p: 0, minWidth: 'auto' }}
                                      >
                                        View
                                      </Button>
                                    ) : (
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>No resume</Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                  {app.status !== 'selected' && <IconButton size="small" onClick={() => handleUpdateStatus(app.id, 'selected')} sx={{ color: '#00783e' }}><CheckCircle fontSize="small" /></IconButton>}
                                  {app.status !== 'rejected' && <IconButton size="small" onClick={() => handleUpdateStatus(app.id, 'rejected')} sx={{ color: '#c62828' }}><Cancel fontSize="small" /></IconButton>}
                                  {app.status !== 'pending'  && <IconButton size="small" onClick={() => handleUpdateStatus(app.id, 'pending')}  sx={{ color: '#856404' }}><HourglassEmpty fontSize="small" /></IconButton>}
                                  <IconButton size="small" onClick={() => handleDeleteApplication(app.id)} sx={{ color: '#6d7175' }}><Delete fontSize="small" /></IconButton>
                                </Box>
                              </Card>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Add Job Dialog ─────────────────────────────────────────────────── */}
      <JobDialog
        open={openAddDialog}
        onClose={handleCloseAdd}
        onSubmit={handleAdd}
        formData={formData}
        formErrors={formErrors}
        onInputChange={handleInputChange}
        onSelectChange={handleSelectChange}
        isLoading={isAdding}
        mode="add"
      />

      {/* ── Edit Job Dialog ────────────────────────────────────────────────── */}
      <JobDialog
        open={openEditDialog}
        onClose={handleCloseEdit}
        onSubmit={handleUpdate}
        formData={formData}
        formErrors={formErrors}
        onInputChange={handleInputChange}
        onSelectChange={handleSelectChange}
        isLoading={isUpdating}
        mode="edit"
      />

      {/* ── Snackbar ──────────────────────────────────────────────────────── */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ── Shared Job Form Dialog ────────────────────────────────────────────────────

interface JobDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  formData: CareerFormData;
  formErrors: Partial<Record<keyof CareerFormData, string>>;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange: (e: SelectChangeEvent) => void;
  isLoading: boolean;
  mode: 'add' | 'edit';
}

const JobDialog: React.FC<JobDialogProps> = ({
  open, onClose, onSubmit, formData, formErrors, onInputChange, onSelectChange, isLoading, mode,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
          {mode === 'add' ? 'Add Job Posting' : 'Edit Job Posting'}
        </Typography>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </Box>
    </DialogTitle>

    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <TextField
          label="Job Title *"
          name="title"
          value={formData.title}
          onChange={onInputChange}
          error={!!formErrors.title}
          helperText={formErrors.title}
          fullWidth
        />
        <TextField
          label="Location"
          name="location"
          value={formData.location ?? ''}
          onChange={onInputChange}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOn fontSize="small" sx={{ color: '#6d7175' }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl fullWidth>
          <InputLabel>Job Type</InputLabel>
          <Select name="type" value={formData.type ?? ''} label="Job Type" onChange={onSelectChange}>
            <MenuItem value=""><em>None</em></MenuItem>
            {JOB_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          label="Experience"
          name="experience"
          value={formData.experience ?? ''}
          onChange={onInputChange}
          placeholder="e.g. 2-4 years"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AccessTime fontSize="small" sx={{ color: '#6d7175' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Description"
          name="description"
          value={formData.description ?? ''}
          onChange={onInputChange}
          fullWidth
          multiline
          rows={4}
          placeholder="Describe the role, responsibilities, and requirements..."
        />
        {mode === 'edit' && (
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select name="is_active" value={String(formData.is_active ?? 1)} label="Status" onChange={onSelectChange}>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="0">Inactive</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>
    </DialogContent>

    <DialogActions sx={{ p: 3, pt: 1 }}>
      <Button onClick={onClose} disabled={isLoading} sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#f44336' }}>
        Close
      </Button>
      <Button onClick={onClose} disabled={isLoading} sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#F78E12' }}>
        Cancel
      </Button>
      <Button onClick={onSubmit} variant="contained" disabled={isLoading} sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#006e52' } }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            {mode === 'add' ? 'Adding...' : 'Updating...'}
          </Box>
        ) : (
          mode === 'add' ? 'Add Job' : 'Update Job'
        )}
      </Button>
    </DialogActions>
  </Dialog>
);

export default CareerManagement;